// EditHistoryPopover.tsx — popover content listing a comment's (or post's) edit versions.
// Generic over any record shaped like { body, time, editHistory }, so it doubles as the
// post edit-history popover (PostMoreMenu / HeroPostCard) without a duplicate component —
// Comment and Post's edit-history entries are byte-identical shapes ({ body, editedAt }).
import styled from 'styled-components';
import { useState } from 'react';
import { Button, MetaRow } from '@/ui';
import { revisionDiff } from '@/features/discussion-tools/revisionDiff';
import { formatRelativeFromIso } from '@/core/lib/formatPostTime';

export interface EditableRecord {
  body: string;
  time: string;
  editHistory: Array<{ body: string; editedAt: string; reason?: string }>;
}

export interface EditHistoryPopoverProps {
  comment: EditableRecord;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 280px;
  padding: 4px;
`;
const Head = styled.div`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Entry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const Label = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Body = styled.div`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: pre-wrap;
  word-break: break-word;
`;

/**
 * Each history entry {body, editedAt} pairs a PRIOR body with the moment it was
 * edited AWAY (not the moment it was written) — see PostViewSet/CommentViewSet
 * .partial_update. So `entries[0].editedAt` is when the FIRST edit happened, not
 * when the original was posted; every row here is therefore labeled "edited
 * {time}", never implied to be that version's own creation time. The Current
 * row uses the LAST entry's time (the most recent edit), not the record's
 * creation time, for the same reason.
 */
export function EditHistoryPopover({ comment }: EditHistoryPopoverProps) {
  const [compare, setCompare] = useState(false);
  const [from, setFrom] = useState(0);
  const entries = comment.editHistory;
  const versions: Array<{ label: string; body: string; time: string }> = [];
  versions.push({
    label: 'Original',
    body: entries[0]?.body ?? comment.body,
    time: entries[0] ? formatRelativeFromIso(entries[0].editedAt) : comment.time,
  });
  for (let i = 1; i < entries.length; i++) {
    versions.push({
      label: `Edit ${i}`,
      body: entries[i]!.body,
      time: formatRelativeFromIso(entries[i]!.editedAt),
    });
  }
  const lastEntry = entries[entries.length - 1];
  versions.push({
    label: 'Current',
    body: comment.body,
    time: lastEntry ? formatRelativeFromIso(lastEntry.editedAt) : comment.time,
  });

  return (
    <Root>
      <Head>Edit history</Head>
      {entries.length > 0 && (
        <Button size="sm" variant="ghost" onClick={() => setCompare(!compare)}>
          {compare ? 'Show all versions' : 'Compare revisions'}
        </Button>
      )}
      {compare ? (
        <>
          <label>
            Compare with current
            <select value={from} onChange={(e) => setFrom(Number(e.target.value))}>
              {entries.map((_, i) => (
                <option key={i} value={i}>
                  {i === 0 ? 'Original' : `Edit ${i}`}
                </option>
              ))}
            </select>
          </label>
          <Label>Struck through: removed. Underlined: added.</Label>
          <Body>
            {revisionDiff(entries[from]?.body ?? comment.body, comment.body).map((c, i) =>
              c.kind === 'removed' ? (
                <del key={i}>{c.text}</del>
              ) : c.kind === 'added' ? (
                <ins key={i}>{c.text}</ins>
              ) : (
                <span key={i}>{c.text}</span>
              ),
            )}
          </Body>
          {lastEntry?.reason && <Body>Reason for latest edit: {lastEntry.reason}</Body>}
        </>
      ) : (
        <>
          {versions.map((v, i) => (
            <Entry key={i}>
              <Label>
                <MetaRow>
                  <span>{v.label}</span>
                  <span>edited {v.time}</span>
                </MetaRow>
              </Label>
              <Body>{v.body}</Body>
            </Entry>
          ))}
        </>
      )}
    </Root>
  );
}
